import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import AuthService from "../Authentication/AuthService";

const MainPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const launchDate = useMemo(() => {
    return new Date(Date.now() + 60 * 24 * 60 * 60 * 1000);
  }, []);

  const [timeLeft, setTimeLeft] = useState({
    days: 60,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date().getTime();
      const distance = launchDate.getTime() - now;

      if (distance <= 0) {
        setTimeLeft({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
        });
        return;
      }

      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000),
      });
    };

    updateCountdown();

    const timer = setInterval(updateCountdown, 1000);

    return () => clearInterval(timer);
  }, [launchDate]);

  const showWaitlistAlert = ({ title, text, icon = "success" }) => {
    Swal.fire({
      icon,
      title,
      text,
      showConfirmButton: true,
      allowOutsideClick: true,
      allowEscapeKey: false,
      customClass: {
        popup: "cie-swal-popup",
        title: "cie-swal-title",
        htmlContainer: "cie-swal-text",
        timerProgressBar: "cie-swal-timer",
      },
    });
  };

  const handleEmailSubmitx = async (e) => {
    e.preventDefault();

    const form = e.currentTarget;

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    if (loading) return;

    const finalEmail = email.trim().toLowerCase();

    try {
      setLoading(true);

      const result = await AuthService.sendUserEmail(finalEmail);

      if (result?.success) {
        setEmail("");

        showWaitlistAlert({
          title: "You're on the waitlist!",
          text: "Thank you for joining our waitlist. We will notify you as soon as Crypto India Exchange goes live.",
        });
      } else {
        setEmail("");

        showWaitlistAlert({
          title: "Already on the waitlist!",
          text: "You are already on our waitlist. We will notify you as soon as Crypto India Exchange goes live.",
        });
      }
    } catch (error) {
      console.error("Error sending email:", error);

      showWaitlistAlert({
        icon: "error",
        title: "Request failed",
        text: error?.response?.data?.message || error?.message || "Unable to submit your email right now. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    const form = e.currentTarget;

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    if (loading) return;

    const finalEmail = email.trim().toLowerCase();

    try {
      setLoading(true);

      const result = await AuthService.sendUserEmail(finalEmail);

      // User successfully added
      if (result?.success === true) {
        setEmail("");

        showWaitlistAlert({
          title: "You're on the waitlist!",
          text: "Thank you for joining our waitlist. We will notify you as soon as Crypto India Exchange goes live.",
        });

        return;
      }

      // API explicitly says user already exists
      if (result?.success === false && (result?.alreadyExists === true || result?.message?.toLowerCase()?.includes("already"))) {
        setEmail("");

        showWaitlistAlert({
          title: "Already on the waitlist!",
          text: "You are already on our waitlist. We will notify you as soon as Crypto India Exchange goes live.",
        });

        return;
      }

      // Unexpected API response
      showWaitlistAlert({
        icon: "error",
        title: "Request Failed",
        text: result?.message || "Unable to submit your email. Please try again later.",
      });
    } catch (error) {
      console.error("Error sending email:", error);

      // Network error / CORS error
      if (!error?.response) {
        showWaitlistAlert({
          icon: "error",
          title: "Connection Error",
          text: "Unable to connect to the server. Please check your connection and try again later.",
        });

        return;
      }

      // Server error (5xx)
      if (error.response.status >= 500) {
        showWaitlistAlert({
          icon: "error",
          title: "Server Error",
          text: error?.response?.data?.message || "Something went wrong on the server. Please try again later.",
        });

        return;
      }

      // Other API errors (400, 401, 403, 404, etc.)
      showWaitlistAlert({
        icon: "error",
        title: "Request Failed",
        text: error?.response?.data?.message || "Unable to submit your email. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  const countdownItems = [
    {
      label: "Days",
      value: timeLeft.days,
    },
    {
      label: "Hours",
      value: timeLeft.hours,
    },
    {
      label: "Minutes",
      value: timeLeft.minutes,
    },
    {
      label: "Seconds",
      value: timeLeft.seconds,
    },
  ];

  return (
    <main>
      <section className="coming_soon_3d min-vh-100 d-flex align-items-center">
        <div className="cs_blur cs_blur_1"></div>
        <div className="cs_blur cs_blur_2"></div>
        <div className="cs_orbit cs_orbit_1"></div>
        <div className="cs_orbit cs_orbit_2"></div>

        <div className="cs_logo_wrap">
          <img src="/assets/cryptoindialogo.svg" alt="Crypto India Exchange" className="cs_logo" />
        </div>

        <div className="container position-relative">
          <div className="row align-items-center justify-content-between g-5 cs_main_row">
            <div className="col-xl-6 col-lg-6 col-12 text-center text-lg-start">
              <h1 className="cs_title mb-3">Something Powerful is Coming Soon</h1>

              <p className="cs_text mb-4">
                Crypto India Exchange is currently under testing. We are building a secure, simple, and reliable crypto experience for India.
              </p>
              <hr />
              <p className="cs_note mb-2">Interested? Leave your email and we will update you first.</p>

              <form onSubmit={handleEmailSubmit} className="cs_form">
                <div className="cs_input_group">
                  <span className="cs_input_icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      color="currentColor"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linejoin="round"
                    >
                      <path d="M2 6L8.91302 9.91697C11.4616 11.361 12.5384 11.361 15.087 9.91697L22 6" />
                      <path d="M2.01577 13.4756C2.08114 16.5412 2.11383 18.0739 3.24496 19.2094C4.37608 20.3448 5.95033 20.3843 9.09883 20.4634C11.0393 20.5122 12.9607 20.5122 14.9012 20.4634C18.0497 20.3843 19.6239 20.3448 20.7551 19.2094C21.8862 18.0739 21.9189 16.5412 21.9842 13.4756C22.0053 12.4899 22.0053 11.5101 21.9842 10.5244C21.9189 7.45886 21.8862 5.92609 20.7551 4.79066C19.6239 3.65523 18.0497 3.61568 14.9012 3.53657C12.9607 3.48781 11.0393 3.48781 9.09882 3.53656C5.95033 3.61566 4.37608 3.65521 3.24495 4.79065C2.11382 5.92608 2.08114 7.45885 2.01576 10.5244C1.99474 11.5101 1.99475 12.4899 2.01577 13.4756Z" />
                    </svg>
                  </span>
                  <input
                    type="email"
                    name="waitlist_email"
                    autoComplete="off"
                    inputMode="email"
                    required
                    disabled={loading}
                    pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
                    title="Please enter a valid email address"
                    className="form-control cs_input"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoFocus
                  />

                  <button type="submit" className="btn cs_btn" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Sending
                      </>
                    ) : (
                      "Notify Me"
                    )}
                  </button>
                </div>
              </form>
              <p className="cs_note cs_note_two mt-3 mb-0">By submitting, you agree to receive launch and product updates.</p>

              <div className="premium_trust_row">
                <div className="premium_trust_item">
                  <span className="premium_trust_icon no-border bg-transperant no-shadow">
                    <img className="img-fluid" alt="" width="40" src="/assets/fiu_india.png" />
                  </span>
                  <div>
                    <strong>FIU-INDIA</strong>
                    <small className="text-muted"> Registration (In-process) </small>
                  </div>
                </div>
                <div className="premium_trust_divider"></div>
                <div className="premium_trust_item">
                  <span className="premium_trust_icon no-border bg-transperant no-shadow">
                    <img className="img-fluid" alt="" width="40" src="/assets/dpdp_icon.svg" />
                  </span>
                  <div>
                    <strong>DPDP</strong>
                    <small>2023</small>
                  </div>
                </div>
                <div className="premium_trust_divider"></div>
                <div className="premium_trust_item">
                  <span className="premium_trust_icon no-border bg-transperant no-shadow">
                    <img className="img-fluid" alt="" width="40" src="/assets/iso_logo.svg" />
                  </span>
                  <div>
                    <strong>ISO/IEC</strong>
                    <small className="text-muted"> 27701:2019 </small>
                    <small className="text-muted mt-0">27001:2022</small>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-5 col-lg-6 col-md-8 col-12">
              <div className="cs_3d_box mx-auto">
                <div className="cs_coin cs_coin_1">₿</div>
                <div className="cs_coin cs_coin_2">Ξ</div>
                <div className="cs_coin cs_coin_3">₹</div>

                <div className="cs_cube">
                  <div className="cs_cube_inner">
                    <h3>Coming Soon</h3>
                    <p>Crypto India Exchange</p>
                  </div>
                </div>

                <div className="cs_shadow"></div>
              </div>
            </div>
          </div>
        </div>

        <footer className="cs_footer position-absolute bottom-0 start-0 w-100 py-3 mt-4 mt-lg-0 ">
          <div className="container">
            <div className="row align-items-center g-4">
              <div className="col-12 col-lg-6">
                <ul className="social-icon justify-content-center justify-content-lg-start ">
                  <li className="social-icon-item">
                    <a
                      href="https://www.youtube.com/@CryptoIndiaExchange"
                      title="Subscribe our Youtube channel"
                      className="social-icon-link youtube_icon"
                      target="_blank"
                    >
                      <i className="ri-youtube-line"></i>
                    </a>
                  </li>
                  <li className="social-icon-item">
                    <a href="https://t.me/CryptoIndiaSupport" title="Follow us on Telergam" className="social-icon-link telegram_icon" target="_blank">
                      <i className="ri-telegram-line"></i>
                    </a>
                  </li>
                  <li className="social-icon-item">
                    <a href="https://x.com/CryptoIndia_in" title="Follow us on X" className="social-icon-link x_icon" target="_blank">
                      <i className="ri-twitter-x-line"></i>
                    </a>
                  </li>
                  <li className="social-icon-item">
                    <a
                      href="https://www.whatsapp.com/channel/0029Vb7oVNoB4hdawvLwoc3y"
                      title="Join us on Whatsapp"
                      className="social-icon-link whatsapp_icon"
                      target="_blank"
                    >
                      <i className="ri-whatsapp-line"></i>
                    </a>
                  </li>
                  <li className="social-icon-item">
                    <a
                      href="https://www.linkedin.com/company/cryptoindia"
                      title="Follow us on Linkedin"
                      className="social-icon-link linkedin_icon"
                      target="_blank"
                    >
                      <span>
                        <i className="ri-linkedin-line"></i>{" "}
                      </span>
                    </a>
                  </li>
                  <li className="social-icon-item">
                    <a
                      href="https://www.instagram.com/cryptoindia_in"
                      title="Follow us on Instagram"
                      className="social-icon-link instagram_icon"
                      target="_blank"
                    >
                      <span>
                        <i className="ri-instagram-line"></i>{" "}
                      </span>
                    </a>
                  </li>
                  <li className="social-icon-item">
                    <a href="https://facebook.com/cryptoindia.in" title="Follow us on Facebook" className="social-icon-link linkedin_icon" target="_blank">
                      <span>
                        <i className="ri-facebook-circle-fill"></i>{" "}
                      </span>
                    </a>
                  </li>
                </ul>
              </div>
              <div className=" col-12  col-lg-6  text-center text-lg-end">
                <p className="copyright-text mb-0">
                  Copyright © {new Date().getFullYear()} <span className="fw-bold">Crypto India</span>. All rights reserved
                </p>
              </div>
            </div>
          </div>
        </footer>
      </section>
    </main>
  );
};

export default MainPage;
